import PropTypes from "prop-types";
import React from "react";
import { connect } from "react-redux";
import { Segment, Header, Message } from "semantic-ui-react";
import {
  Icon,
  Button,
  Popconfirm,
  Row,
  Col,
  notification,
  Modal,
  Typography,
  message,
} from "antd";
import {
  isApiCallLoading,
  selectAccountManagementOn,
  selectCernAccountProvider,
} from "../../selectors";
import { withApiCapabilities } from "../../wrappers";
import ReassignAccountModal from "./ReassignAccountModal";
import ChangePasswordModal from "./passwordManagement/ChangePasswordModal";
import ResetPasswordModal from "./passwordManagement/ResetPasswordModal";
import { errorAction, types as at } from "@authzsvc/api-lib";
import { isSecondaryOrService, isService } from "../../utils";
import {
  changePassword,
  resetPassword,
  activateAccount,
} from "../../actions/api";
import { showErrorNotification } from "../../utils";

const AccountEditActions = ({
  identity,
  identityOwner,
  account,
  loading,
  accountManagementOn,
  cernAccountProvider,
  onUpdateIdentity, // prop function called after identity has been updated
  // redux Actions
  activateAccount,
  changePassword,
  resetPassword,
  userToken,
}) => {
  const onResetPassword = async (newPassword, account) => {
    const resetResp = resetPassword(account.id, newPassword);
    if (resetResp.type === errorAction(at.RESET_PASSWORD)) {
      showErrorNotification(
        resetResp.payload.title,
        resetResp.payload.data.message
      );
    }
    return resetResp;
  };

  const onActivateAccount = async (account) => {
    const response = await activateAccount(account.id);
    if (response.type === errorAction(at.ACTIVATE_ACCOUNT)) {
      showErrorNotification(
        response.payload.title,
        response.payload.data.message
      );
    } else {
      onUpdateIdentity(identity.id);

      const { newPassword, newEDHPassword } = response.payload.data;
      showPasswordModal({ newPassword, newEDHPassword });

      notification.success({
        message: "Account activated succesfully!",
        description:
          "An intial password for your account has been successfully set and instructions have been sent to your email.",
      });
    }
    return response;
  };

  const onChangePassword = async (oldPassword, newPassword, account) => {
    const changeResp = await changePassword(
      account.id,
      oldPassword,
      newPassword
    );
    if (changeResp.type === errorAction(at.CHANGE_PASSWORD)) {
      showErrorNotification(
        changeResp.payload.title,
        changeResp.payload.data.message
      );
    }
    return changeResp;
  };

  const actionsPermitted = () => {
    // If it's a CERN secondary/service account owned by the user, or the person's own primary
    if (
      cernAccountProvider &&
      account &&
      identity &&
      ((isSecondaryOrService(identity) &&
        identityOwner.upn === userToken.sub) ||
        identity.upn === userToken.sub) &&
      account.accountProviderId === cernAccountProvider.id
    ) {
      return true;
    } else {
      return false;
    }
  };

  return (
    <>
      {
        <Segment basic color="blue">
          <Header as="h3">
            <span>
              <Icon name="setting" />
              Actions
            </span>
          </Header>
          {actionsPermitted() ? (
            <>
              <Row gutter={[0, 16]} type="flex" direction="vertical">
                {/* If not blocked, can update password */}
                {!identity.blocked && !account.blocked && (
                  <>
                    {/* Can only reset secondary and service accounts */}
                    {isSecondaryOrService(identity) && (
                      <Col span={24}>
                        <ResetPasswordModal
                          loading={loading}
                          onSubmit={(newPassword) =>
                            onResetPassword(newPassword, account)
                          }
                          identity={identity}
                          title="Reset password"
                        />
                      </Col>
                    )}
                    <Col span={24}>
                      {/* Can change primary/secondar/service passwords */}
                      <ChangePasswordModal
                        loading={loading}
                        onSubmit={(oldPassword, newPassword) =>
                          onChangePassword(oldPassword, newPassword, account)
                        }
                        identity={identity}
                        title="Change password"
                      />
                    </Col>
                  </>
                )}
                {/* If not blocked, can set initial password for secondary/service if not yet activated */}
                {!identity.blocked &&
                  account.blocked &&
                  account.internalState === "Created" &&
                  isSecondaryOrService(identity) && (
                    <Col span={24}>
                      <Popconfirm
                        title="Are you sure you want to do activate the account?"
                        onConfirm={() => onActivateAccount(account)}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Button type="primary" icon="login" block size="large">
                          Activate account
                        </Button>
                      </Popconfirm>
                    </Col>
                  )}
                {/* If account management on, can reassign service identities */}
                {accountManagementOn && (
                  <Col span={24}>
                    {isService(identity) && (
                      <ReassignAccountModal identity={identity} />
                    )}
                  </Col>
                )}
              </Row>
            </>
          ) : (
            <>
              <Message>
                <Message.Header>No Actions Available</Message.Header>
                <p>
                  This is not a CERN Account, or you are not the account owner.
                </p>
              </Message>
            </>
          )}
        </Segment>
      }
    </>
  );
};

const showPasswordModal = ({ newPassword, newEDHPassword }) => {
  const renderPasswordBlock = (label, value, labelForCopy) => (
    <Row type="flex" align="middle" style={{ marginTop: 12 }}>
      <Col>
        <Typography.Text strong style={{ lineHeight: "32px", fontSize: 16 }}>
          {label}
        </Typography.Text>
      </Col>
      <Col style={{ marginLeft: 8 }}>
        <Typography.Paragraph
          copyable={{
            text: value,
            onCopy: () => message.success(`${labelForCopy} copied`),
          }}
          code
          style={{ marginBottom: 10, fontSize: 18 }}
        >
          {value}
        </Typography.Paragraph>
      </Col>
    </Row>
  );

  Modal.info({
    title: (
      <span>
        <Icon type="user" style={{ color: "#543830", marginRight: 8 }} />
        <Typography.Text strong style={{ fontSize: 16 }}>
          New Account Password
        </Typography.Text>
      </span>
    ),
    content: (
      <div style={{ marginTop: 40 }}>
        {renderPasswordBlock(
          "Initial Password:",
          newPassword,
          "Initial password"
        )}
        {newEDHPassword &&
          renderPasswordBlock(
            "Initial EDH Password:",
            newEDHPassword,
            "Initial EDH password"
          )}
        <Typography.Paragraph type="secondary" style={{ marginTop: 16 }}>
          Please store this information securely. These values won't be shown
          again.
        </Typography.Paragraph>
      </div>
    ),
    okText: "Got it",
    okButtonProps: {
      style: {
        backgroundColor: "#543830",
        borderColor: "#543830",
        color: "#fff",
      },
    },
    icon: null,
  });
};

AccountEditActions.propTypes = {
  identity: PropTypes.object.isRequired,
  account: PropTypes.object.isRequired,
  cernAccountProvider: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  accountManagementOn: PropTypes.bool.isRequired,
  userToken: PropTypes.object.isRequired,
  onUpdateIdentity: PropTypes.func.isRequired,
};

export default withApiCapabilities(
  connect(
    (state, ownProps) => ({
      accountManagementOn: selectAccountManagementOn(ownProps.capabilities),
      loading:
        isApiCallLoading(state, at.SET_INITIAL_PASSWORD) ||
        isApiCallLoading(state, at.CHANGE_PASSWORD) ||
        isApiCallLoading(state, at.RESET_PASSWORD),
      cernAccountProvider: selectCernAccountProvider(state),
      userToken: state.keycloak.userToken,
    }),
    {
      changePassword,
      resetPassword,
      activateAccount,
    }
  )(AccountEditActions)
);
